import React from 'react'
import { useQueries } from 'react-query'
import ProfessionalChartLoader from '../../../../common/components/molecule/Chart/ChartLoaderView/ProfessionalChartLoader'
import ChartErrorView from '../../../../common/components/molecule/Chart/ChartLoaderView/ChartErrorView'
import { createList } from '../../../../common/utils/graph.utils'
import { formatDateByPeriod } from '../../../../common/utils/dateFormatter'
import ChartLineView from '../../../../common/components/molecule/Chart/ChartLineView/ChartLineView'
import { AggregateWindow } from '../../../../features/filter/types/types'
import { getMomiTelemetryMeasurement, MomiTelemetryFilter } from '../../service/momiTelemetryAPI'
import { EChartsSeries } from '../../../../common/components/molecule/EChartsView/EChartsView'
import { MOMI_SERIES_COLORS } from './momiTelemetryChart.utils'
import { MomiChartLine } from '../../liveEnergyDashboardConfig'

type Props = {
  momiUuid: string
  title: string
  unit: string
  lines: MomiChartLine[]
  filter: MomiTelemetryFilter
  scale?: number
}

/**
 * Plusieurs mesures Influx (ex. phase 1/2/3 Schneider) dans un seul graphique ECharts.
 */
export default function MomiTelemetryMultiLineChartPresenter({
  momiUuid,
  title,
  unit,
  lines,
  filter,
  scale,
}: Props) {
  const queries = useQueries(
    lines.map((line) => ({
      queryKey: ['momi-telemetry', momiUuid, line.measurement, filter] as const,
      queryFn: () => getMomiTelemetryMeasurement(momiUuid, line.measurement, filter),
      enabled: Boolean(momiUuid && line.measurement),
    }))
  )

  const loading = queries.some((q) => q.isLoading)
  const hasError = queries.some((q) => q.isError)

  if (loading) {
    return <ProfessionalChartLoader metricName={title} />
  }

  if (hasError) {
    return <ChartErrorView metricName={title} />
  }

  const aggWindow = (filter.aggregateWindow || '1h') as AggregateWindow
  const filterPoint = {
    startDate: filter.startDate,
    stopDate: filter.stopDate,
    aggregateWindow: aggWindow,
  }

  const firstPoints = queries[0]?.data ?? []
  const { listx } = createList(aggWindow, filterPoint, firstPoints)
  const xLabels = listx.map((item) => formatDateByPeriod(item, aggWindow))

  const multiSeries: EChartsSeries[] = lines.map((line, idx) => {
    const points = queries[idx]?.data ?? []
    const { listValue } = createList(aggWindow, filterPoint, points)
    return {
      name: line.label,
      color: MOMI_SERIES_COLORS[idx % MOMI_SERIES_COLORS.length],
      data: xLabels.map((name, i) => {
        const raw = listValue[i] ?? null
        const value = raw != null && scale != null ? raw * scale : raw
        return { name, value }
      }),
    }
  })

  return (
    <ChartLineView
      multiSeries={multiSeries}
      xAxisData={xLabels}
      data={[]}
      label={title}
      unit={unit}
    />
  )
}
